/* MITOPDEAL predictive search
 * Drives live suggestions for the custom Marketplace header search field.
 * Uses Shopify's native /search/suggest.json endpoint - no app, no dependencies.
 *
 * The header block wraps its input in <predictive-search> expecting Dawn's
 * script to drive it, but Dawn's header section is disabled so the supporting
 * CSS/JS never loads. We attach to the input directly and hide the block's
 * empty native container so only one dropdown ever renders.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;
  var LIMIT = 6;

  var css = [
    '[data-predictive-search]{display:none !important}',
    '.mtd-ps{position:absolute;z-index:10001;left:0;right:0;top:calc(100% + 8px);background:#fff;',
    'border:1px solid #D6DEE9;border-radius:8px;box-shadow:0 8px 24px rgba(11,37,69,.14);',
    'display:none;max-height:70vh;overflow-y:auto;text-align:left}',
    '.mtd-ps.is-open{display:block}',
    '.mtd-ps__group{padding:8px 0}',
    '.mtd-ps__label{font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#64748B;',
    'padding:8px 16px 4px;font-weight:600}',
    '.mtd-ps__item{display:flex;align-items:center;gap:12px;padding:8px 16px;text-decoration:none;',
    'color:#0F172A;min-height:44px}',
    '.mtd-ps__item:hover,.mtd-ps__item.is-active{background:#EAF1FE}',
    '.mtd-ps__img{width:44px;height:44px;object-fit:contain;border-radius:4px;background:#F6F8FB;flex:0 0 auto}',
    '.mtd-ps__txt{min-width:0;flex:1 1 auto}',
    '.mtd-ps__title{font-size:14px;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;',
    '-webkit-box-orient:vertical;overflow:hidden}',
    '.mtd-ps__price{font-size:13px;font-weight:700;color:#0B2545;font-variant-numeric:tabular-nums;margin-top:2px}',
    '.mtd-ps__all{display:block;padding:12px 16px;font-size:14px;font-weight:600;color:#1B5FE3;',
    'text-decoration:none;border-top:1px solid #D6DEE9}',
    '.mtd-ps__all:hover,.mtd-ps__all.is-active{background:#EAF1FE}',
    '.mtd-ps__empty{padding:16px;font-size:14px;color:#64748B}'
  ].join('');

  function injectCss() {
    if (document.getElementById('mtd-ps-css')) return;
    var s = document.createElement('style');
    s.id = 'mtd-ps-css';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function money(cents) {
    var n = Number(cents);
    if (isNaN(n)) return '';
    return '$' + (n / 100).toFixed(2);
  }

  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  function build(input) {
    if (input.getAttribute('data-mtd-ps') === '1') return;
    input.setAttribute('data-mtd-ps', '1');

    var host = input.closest('form') || input.parentElement;
    if (!host) return;
    if (window.getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    var panel = document.createElement('div');
    panel.className = 'mtd-ps';
    panel.setAttribute('role', 'listbox');
    host.appendChild(panel);

    var timer = null;
    var controller = null;
    var lastQuery = '';

    function close() {
      panel.classList.remove('is-open');
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    }

    function render(data, q) {
      var results = (data && data.resources && data.resources.results) || {};
      var products = results.products || [];
      var collections = results.collections || [];
      var html = '';

      if (collections.length) {
        html += '<div class=\'mtd-ps__group\'><div class=\'mtd-ps__label\'>Categories</div>';
        collections.forEach(function (c) {
          html += '<a class=\'mtd-ps__item\' role=\'option\' href=\'' + esc(c.url) + '\'>' +
            '<div class=\'mtd-ps__txt\'><div class=\'mtd-ps__title\'>' + esc(c.title) + '</div></div></a>';
        });
        html += '</div>';
      }

      if (products.length) {
        html += '<div class=\'mtd-ps__group\'><div class=\'mtd-ps__label\'>Products</div>';
        products.forEach(function (p) {
          var img = (p.featured_image && p.featured_image.url) || '';
          var alt = (p.featured_image && p.featured_image.alt) || p.title || '';
          html += '<a class=\'mtd-ps__item\' role=\'option\' href=\'' + esc(p.url) + '\'>';
          if (img) {
            html += '<img class=\'mtd-ps__img\' src=\'' + esc(img) + '\' alt=\'' + esc(alt) +
              '\' loading=\'lazy\' width=\'44\' height=\'44\'>';
          }
          html += '<div class=\'mtd-ps__txt\'><div class=\'mtd-ps__title\'>' + esc(p.title) + '</div>';
          if (p.price != null) {
            html += '<div class=\'mtd-ps__price\'>' + money(p.price) + '</div>';
          }
          html += '</div></a>';
        });
        html += '</div>';
      }

      if (!html) {
        html = '<div class=\'mtd-ps__empty\'>No matches for \u201c' + esc(q) + '\u201d</div>';
      }

      html += '<a class=\'mtd-ps__all\' href=\'/search?q=' + encodeURIComponent(q) + '\'>' +
        'See all results for \u201c' + esc(q) + '\u201d</a>';

      panel.innerHTML = html;
      panel.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
    }

    function fetchResults(q) {
      if (controller) controller.abort();
      controller = new AbortController();

      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection' +
        '&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) { if (data) render(data, q); })
        .catch(function (err) { if (err && err.name !== 'AbortError') close(); });
    }

    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-expanded', 'false');

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (q === lastQuery) return;
      lastQuery = q;
      clearTimeout(timer);
      if (q.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(function () { fetchResults(q); }, DEBOUNCE_MS);
    });

    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length >= MIN_CHARS && !panel.classList.contains('is-open')) fetchResults(q);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var items = panel.querySelectorAll('.mtd-ps__item, .mtd-ps__all');
      if (!items.length) return;
      e.preventDefault();
      var cur = panel.querySelector('.is-active');
      var idx = cur ? Array.prototype.indexOf.call(items, cur) : -1;
      if (cur) cur.classList.remove('is-active');
      if (e.key === 'ArrowDown') {
        idx = (idx + 1) % items.length;
      } else {
        idx = idx <= 0 ? items.length - 1 : idx - 1;
      }
      items[idx].classList.add('is-active');
      items[idx].focus();
    });

    document.addEventListener('click', function (e) {
      if (!host.contains(e.target)) close();
    });
  }

  function scan() {
    injectCss();
    var inputs = document.querySelectorAll('input[name=\'q\']');
    Array.prototype.forEach.call(inputs, function (input) {
      build(input);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:block:select', scan);
  window.addEventListener('load', scan);
})();
